import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Eye, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "starter", label: "Starter (default)" },
  { value: "house-rules", label: "House rules" },
  { value: "amenities", label: "Amenities" },
];

/**
 * MoveInKitPreviewDialog — live proof of the exact HTML email a new resident
 * will receive. Admins can flip the headline category and the preview
 * refetches instantly so they see the real email before hitting Send.
 */
export function MoveInKitPreviewDialog({ open, onOpenChange, user, onSent }) {
  const [loading, setLoading] = useState(false);
  const [html, setHtml] = useState("");
  const [busy, setBusy] = useState(false);
  const [category, setCategory] = useState("starter");

  const propertyId = (user?.property_ids || [])[0];

  const loadPreview = useCallback(async () => {
    if (!user) return;
    if (!propertyId) {
      setHtml("<div style='padding:24px;color:#a1a1aa;font-family:system-ui;'>Resident has no community assigned — assign one before sending.</div>");
      return;
    }
    setLoading(true);
    try {
      const res = await api.get("/admin/movein-kit/preview", {
        params: { property_id: propertyId, headline_category: category },
        responseType: "text",
      });
      setHtml(res.data || "");
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load preview"); }
    setLoading(false);
  }, [user, propertyId, category]);

  useEffect(() => { if (open) loadPreview(); }, [open, loadPreview]);

  // Reset category to default when the dialog closes so the next open
  // starts fresh rather than remembering the previous admin's choice.
  useEffect(() => { if (!open) setCategory("starter"); }, [open]);

  const send = async () => {
    if (!user) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${user.id}/send-movein-kit`, null, {
        params: { headline_category: category },
      });
      toast.success(`Move-in kit sent · ${data.wiki_pages} pages + ${data.perks} perks (${data.headline_category})`, {
        duration: 4500,
        id: `movein-sent-${user.id}`,
        description: `Delivered to ${user.email}`,
      });
      onSent?.();
      onOpenChange(false);
    } catch (e) { toast.error(apiErrorMessage(e) || "Send failed"); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[720px] max-h-[90vh] overflow-hidden flex flex-col" data-testid="movein-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-4 w-4 text-orange-400" /> Move-in kit preview
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Exactly what {user?.email || "the resident"} will receive. Flip the headline category to preview each variant before hitting send.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 mb-2">
          <Badge variant="outline" className="border-white/10 text-zinc-400">
            To: {user?.email || "—"}
          </Badge>
          {(user?.property_ids || []).length > 0 && (
            <Badge variant="outline" className="border-orange-500/40 text-orange-300">
              {(user?.property_ids || []).length} community
            </Badge>
          )}
          <div className="flex items-center gap-2 ml-auto">
            <Label className="text-[11px] text-zinc-500 shrink-0">Headline</Label>
            <Select value={category} onValueChange={setCategory} disabled={!propertyId || busy}>
              <SelectTrigger className="w-44 h-8 bg-white/[0.04] border-white/10"
                              data-testid="movein-preview-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                {CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}
                                data-testid={`movein-preview-cat-${c.value}`}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden rounded-md border border-white/10 bg-white relative" data-testid="movein-preview-frame">
          {loading && (
            <div className="absolute inset-x-0 top-0 bg-black/80 text-zinc-300 text-[11px] px-3 py-1 flex items-center gap-2 z-10">
              <Loader2 className="h-3 w-3 animate-spin" /> Re-rendering with {CATEGORIES.find((c) => c.value === category)?.label || category}…
            </div>
          )}
          <iframe
            title="Move-in kit preview"
            srcDoc={html}
            className="w-full h-full min-h-[500px] border-0"
            sandbox=""
            data-testid="movein-preview-iframe"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="border-white/10">
            Close
          </Button>
          <Button onClick={send} disabled={busy || loading}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="movein-preview-send">
            {busy ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Sending…</>
                  : <><Send className="h-3 w-3 mr-1" /> Send {CATEGORIES.find((c) => c.value === category)?.label.toLowerCase() || "starter"} kit</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default MoveInKitPreviewDialog;
